import { FormListFieldColumnDefinition, LabelMode } from '@msdk/definitions'

/**
 * Determines whether the header should be shown for a list field component.
 *
 * @param hideHeader - If `true`, the header will be hidden regardless of other settings.
 * @param columns - An optional array of column definitions, each of which may specify a `labelMode`.
 * @param defaultColumnStyleLabelMode - The default label mode to use if a column does not specify one.
 * @returns `true` if the header should be shown, otherwise `false`.
 *
 * The logic considers the following:
 * - If `hideHeader` is `true`, the header is always hidden.
 * - If any column explicitly defines a `labelMode`, the function checks if either the default label mode or
 *   any column's label mode requires the header to be shown.
 * - If no column defines a `labelMode`, the function falls back to the default label mode.
 */
export function shouldShowHeader(
  hideHeader: boolean | undefined,
  columns: { labelMode?: LabelMode | null }[] | undefined,
  defaultColumnStyleLabelMode: LabelMode | undefined,
): boolean {
  if (hideHeader === true) {
    return false
  }

  const cols = columns || []
  const getEffectiveLabelMode = (mode: LabelMode | null | undefined) =>
    mode !== undefined && mode !== null && mode !== LabelMode.Undefined ? mode : undefined

  const defaultLabelMode = getEffectiveLabelMode(defaultColumnStyleLabelMode) ?? LabelMode.ShowBoth

  const anyColumnHasLabelMode = cols.some((c) => getEffectiveLabelMode(c.labelMode) !== undefined)

  const anyColumnNeedsHeader = cols.some((c) => {
    const mode = getEffectiveLabelMode(c.labelMode)
    return mode === LabelMode.ShowHeader || mode === LabelMode.ShowBoth
  })

  if (anyColumnHasLabelMode) {
    if (defaultLabelMode === LabelMode.ShowHeader || defaultLabelMode === LabelMode.ShowBoth) {
      return true
    }
    return anyColumnNeedsHeader
  }

  return defaultLabelMode === LabelMode.ShowHeader || defaultLabelMode === LabelMode.ShowBoth
}

/**
 * Resolves the label mode for a column definition by checking the provided `colDef` and `defaultColumnStyle` objects.
 *
 * The function returns the first defined and non-`LabelMode.Undefined` value found in `colDef.labelMode` or `defaultColumnStyle.labelMode`.
 * If neither is set, it defaults to `LabelMode.ShowBoth`.
 *
 * @param colDef - The column definition object which may contain a `labelMode` property.
 * @param defaultColumnStyle - The default column style object which may contain a `labelMode` property.
 * @returns The resolved `LabelMode` value.
 */
export function resolveLabelMode(
  colDef: FormListFieldColumnDefinition | undefined,
  defaultColumnStyle: FormListFieldColumnDefinition,
): LabelMode {
  if (colDef?.labelMode !== undefined && colDef?.labelMode !== null && colDef?.labelMode !== LabelMode.Undefined) {
    return colDef.labelMode
  }
  if (
    defaultColumnStyle?.labelMode !== undefined &&
    defaultColumnStyle?.labelMode !== null &&
    defaultColumnStyle?.labelMode !== LabelMode.Undefined
  ) {
    return defaultColumnStyle.labelMode
  }
  return LabelMode.ShowBoth
}

/**
 * Determines whether the header cell should be displayed for a given column definition.
 *
 * @param colDef - The column definition object.
 * @param defaultColumnStyle - The default style settings for the column.
 * @returns `true` if the header cell should be shown, otherwise `false`.
 *
 * @remarks
 * This function resolves the label mode for the column and checks if it is set to either
 * `ShowBoth` or `ShowHeader` from the `LabelMode` enum.
 */
export const shouldShowHeaderCell = (
  colDef: FormListFieldColumnDefinition | undefined,
  defaultColumnStyle: FormListFieldColumnDefinition,
): boolean => {
  const labelMode = resolveLabelMode(colDef, defaultColumnStyle)
  return labelMode === LabelMode.ShowBoth || labelMode === LabelMode.ShowHeader
}

/**
 * Determines whether the cell label should be shown based on the provided column definition
 * and a default column style.
 *
 * The function checks the `labelMode` property in the following order of precedence:
 * 1. If `colDef.labelMode` is defined, not null, and not `LabelMode.Undefined`, it is used.
 * 2. Otherwise, if `defaultColumnStyle.labelMode` is defined, not null, and not `LabelMode.Undefined`, it is used.
 * 3. Otherwise, defaults to `LabelMode.ShowBoth`.
 *
 * Returns `true` if the resolved `labelMode` is either `LabelMode.ShowBoth` or `LabelMode.ShowIntoCell`.
 *
 * @param colDef - The column definition which may specify a `labelMode`.
 * @param defaultColumnStyle - The default column style to use if `colDef.labelMode` is not set.
 * @returns `true` if the cell label should be shown, otherwise `false`.
 */
export function shouldShowCellLabel(
  colDef: FormListFieldColumnDefinition | undefined,
  defaultColumnStyle: FormListFieldColumnDefinition,
): boolean {
  const labelMode =
    colDef?.labelMode !== undefined && colDef?.labelMode !== null && colDef?.labelMode !== LabelMode.Undefined
      ? colDef.labelMode
      : defaultColumnStyle?.labelMode !== undefined &&
        defaultColumnStyle?.labelMode !== null &&
        defaultColumnStyle?.labelMode !== LabelMode.Undefined
      ? defaultColumnStyle.labelMode
      : LabelMode.ShowBoth

  return labelMode === LabelMode.ShowBoth || labelMode === LabelMode.ShowIntoCell
}
